import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, Pause, Swords, Trophy } from "lucide-react";
import { api, media, deviceId, errMsg, fmtDate } from "@/lib/api";
import { SectionHead } from "@/components/fo/Bits";

const Side = ({ s, side, b, onVote, playing, onPlay }) => {
  const total = b.votes.a + b.votes.b || 1;
  const pct = Math.round((b.votes[side] / total) * 100);
  const mine = b.my_vote === side;
  const won = b.status === "done" && b.winner === side;
  return (
    <div className={`panel relative overflow-hidden ${mine || won ? "border-[#FF2E63]/70 glow-crimson" : ""}`} data-testid={`battle-side-${side}`}>
      <div className="relative aspect-[4/5]">
        {s.artist.photo && <img src={media(s.artist.photo)} alt={s.artist.stage_name} className="h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] via-[#07090E]/30 to-transparent" />
        {won && <span className="absolute left-4 top-4 flex items-center gap-1 rounded-full bg-[#FFB800] px-3 py-1 font-mono text-[10px] uppercase text-[#07090E]"><Trophy className="h-3 w-3" />Winner</span>}
        <div className="absolute inset-x-0 bottom-0 p-5">
          <Link to={`/a/${s.artist.slug}`} className="font-display text-3xl font-black uppercase leading-none hover:text-[#00F5D4] sm:text-4xl">{s.artist.stage_name}</Link>
          <div className="mt-1 text-slate-300">"{s.title}"</div>
        </div>
      </div>
      <div className="space-y-4 p-5">
        <div className="flex items-center gap-3">
          <button onClick={onPlay} data-testid={`battle-play-${side}`} className="flex h-12 w-12 items-center justify-center rounded-full border border-[#00F5D4]/60 text-[#00F5D4] transition-colors hover:bg-[#00F5D4] hover:text-[#07090E]">{playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}</button>
          <div className="flex-1"><div className="h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-gradient-to-r from-[#00F5D4] to-[#FF2E63] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div></div>
          <span className="led w-16 text-right text-2xl font-bold" data-testid={`battle-pct-${side}`}>{pct}%</span>
        </div>
        {b.status === "active" && (
          <button disabled={!!b.my_vote} onClick={onVote} data-testid={`battle-vote-${side}`} className={mine ? "btn-hot w-full" : "btn-ghost w-full disabled:opacity-40"}>
            {mine ? "Your vote" : b.my_vote ? "Voted" : `Vote ${s.artist.stage_name}`}
          </button>
        )}
      </div>
    </div>
  );
};

export default function Battle() {
  const [b, setB] = useState(undefined);
  const [past, setPast] = useState([]);
  const [playing, setPlaying] = useState(null);
  const audio = useRef(null);
  const load = useCallback(() => api.get("/battles/current", { params: { device_id: deviceId() } }).then((r) => setB(r.data)), []);
  useEffect(() => {
    load();
    api.get("/battles").then((r) => setPast(r.data));
    const id = setInterval(load, 5000);
    return () => { clearInterval(id); audio.current?.pause(); };
  }, [load]);

  const play = (side) => {
    if (!audio.current) audio.current = new Audio();
    if (playing === side) { audio.current.pause(); return setPlaying(null); }
    audio.current.src = media(b[side].audio);
    audio.current.play();
    setPlaying(side);
  };
  const vote = async (side) => {
    try { const { data } = await api.post(`/battles/${b.id}/vote`, { device_id: deviceId(), side }); setB(data); toast.success("Vote counted"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (b === undefined) return <div className="pt-40 text-center font-mono text-slate-500">Loading…</div>;
  return (
    <div className="mx-auto max-w-6xl px-4 pb-40 pt-28 md:px-8" data-testid="battle-page">
      <SectionHead label={b ? `${b.status === "active" ? "Live until" : "Ended"} ${fmtDate(b.end)}` : "Monthly"} title={b?.title || "Battle"}>
        Two artists, two tracks, one winner. Listen to both, then vote — one vote per device. The winner gets 1 month of Pro and the Battle Winner badge.
      </SectionHead>
      {!b ? <p className="py-16 text-center text-slate-500" data-testid="battle-empty">No battle running right now. The next one starts on the 1st of the month.</p> : (
        <div className="relative grid gap-6 md:grid-cols-2">
          <Side s={b.a} side="a" b={b} playing={playing === "a"} onPlay={() => play("a")} onVote={() => vote("a")} />
          <div className="pointer-events-none absolute left-1/2 top-1/3 z-10 hidden h-20 w-20 -translate-x-1/2 items-center justify-center rounded-full bg-[#FF2E63] font-display text-2xl font-black md:flex"><Swords className="h-8 w-8" /></div>
          <Side s={b.b} side="b" b={b} playing={playing === "b"} onPlay={() => play("b")} onVote={() => vote("b")} />
        </div>
      )}
      {b && <p className="mt-4 text-center font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="battle-total">{b.votes.a + b.votes.b} votes · live</p>}
      {past.length > 0 && (
        <div className="mt-20">
          <div className="label-mono mb-4">Past battles</div>
          {past.map((p) => (
            <div key={p.id} className="flex flex-wrap justify-between gap-2 border-b border-white/5 py-3 text-sm">
              <span className="font-display font-bold">{p.a?.artist.stage_name} vs {p.b?.artist.stage_name}</span>
              <span className="font-mono text-xs text-slate-400">{p.votes.a}–{p.votes.b} · winner {p.winner ? p[p.winner]?.artist.stage_name : "tie"} · {fmtDate(p.end)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
